import { toast } from 'vue-sonner'

import axios, { type InternalAxiosRequestConfig } from 'axios'

import { router } from '@/router'
import { useUserStore } from '@/stores/user'
import { isTokenExpired, shouldRefreshToken } from '@/utils/tokenExpiry'

const service = axios.create({
  baseURL: import.meta.env.VITE_APP_PUBLIC_API_HTTP_URL,
  timeout: 15000,
  // withCredentials: true // send cookies when cross-domain requests
})

// Promise to track ongoing refresh to prevent multiple simultaneous refreshes
let refreshPromise: Promise<string | void> | null = null

async function handleExpiredSession(): Promise<never> {
  const userStore = useUserStore()
  await userStore.resetToken()
  router.push('/login')
  return Promise.reject(new Error('Session expired'))
}

async function tryRefreshToken(refreshToken: string): Promise<string | undefined> {
  const userStore = useUserStore()

  if (!refreshPromise) {
    refreshPromise = userStore.refreshTokenAction(refreshToken).finally(() => {
      refreshPromise = null
    })
  }

  const newToken = await refreshPromise
  return newToken || undefined
}

function setAuthHeader(config: InternalAxiosRequestConfig, token: string | undefined): void {
  if (token) {
    config.headers['Authorization'] = token
  }
}

// Request interceptors
service.interceptors.request.use(
  async config => {
    config.headers['Content-Type'] = 'application/json'

    const userStore = useUserStore()

    // Skip token expiry checks for auth endpoints to avoid circular dependency
    if (config.url?.startsWith('/auth/')) {
      setAuthHeader(config, userStore.token)
      return config
    }

    let token = userStore.token
    const refreshToken = userStore.refreshToken

    // Check if both tokens are expired - redirect to login
    if (isTokenExpired(token) && isTokenExpired(refreshToken)) {
      return handleExpiredSession()
    }

    // Proactive refresh if token will expire soon
    if (shouldRefreshToken(token) && !isTokenExpired(refreshToken)) {
      try {
        const newToken = await tryRefreshToken(refreshToken)
        if (newToken) token = newToken
      } catch {
        // Refresh failed, redirect if token is also expired
        if (isTokenExpired(token)) return handleExpiredSession()
      }
    }

    setAuthHeader(config, token)
    return config
  },
  error => Promise.reject(error),
)

// Response interceptors
service.interceptors.response.use(
  response => response,
  error => {
    let message = ''
    console.error('Error', error.response)
    if (error.response.status === 401) {
      message = error.response.data.errors[0].message
    } else if (error.response.status === 400 && error.response.data.error === 'invalid_grant') {
      message = error.response.data.errors[0].message
    } else if (error.response.status === 500) {
      message = error.response?.data.error
    } else {
      message = error.response ? error.response.data.errors[0].message : error.message
    }

    toast.error(message)

    return Promise.reject(error)
  },
)

export default service
